/**
 * Resolves the viewer's country / city / lat / lng for the Events page's
 * default-filter + "Events in <city>" section.
 *
 * Fallback chain (server-side, see UserController.resolveViewerGeo):
 *   1. IP geolocation
 *   2. Logged-in user's saved profile (yr_users.country / city /
 *      map_lat / map_lng)
 *   3. Both unknown → every field is null, source='none' → UI falls
 *      back to showing ALL events with no city section.
 *
 * Cached for the browser session via TanStack's default — no need to
 * re-fetch on every page nav. Backend caches the IP → geo lookup too.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';

export interface ViewerGeo {
  country: string | null; // ISO alpha-2
  city: string | null;
  lat: number | null;
  lng: number | null;
  source: 'ip' | 'profile' | 'ip+profile' | 'none';
}

export function useViewerGeo() {
  return useQuery<ViewerGeo>({
    queryKey: ['user', 'geo'],
    queryFn: () => api.get<ViewerGeo>('user/geo'),
    staleTime: 30 * 60 * 1000, // 30 min — IP + profile don't change often
    retry: 1,
  });
}
